import { kebabCase, noop } from 'lodash/fp';
import { ReactElement, ReactNode, useCallback, useContext } from 'react';
import { ThemeContext } from 'styled-components';

import ActionsNode from 'dashboard/apps/InquiryTemplateEditor/ActionsNode';
import CompleteStepNode from 'dashboard/apps/InquiryTemplateEditor/CompleteStepNode';
import ConditionalNode from 'dashboard/apps/InquiryTemplateEditor/ConditionalNode';
import ConditionalRootNode from 'dashboard/apps/InquiryTemplateEditor/ConditionalRootNode';
import InquiryRunTreeNavigation from 'dashboard/apps/InquiryTemplateEditor/InquiryRunTree/InquiryRunTreeNavigation';
import {
  CombinedRunStepForTree,
  WorkflowVersionMap,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/types';
import {
  getConditionalStepForToken,
  getNodeWrapperAttributes,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/utils';
import InquiryTemplateScreenPreview from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenPreview';
import { useInquiryTemplateTreeContent } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/InquiryTemplateTreeProvider';
import {
  calculateScreenFlowPadding,
  ConditionalWrapper,
  stepContentHeight,
  stepContentWidth,
  workflowErrorRouteDescription,
  workflowErrorRouteLabel,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateTree/shared';
import ScreenFlow from 'dashboard/apps/InquiryTemplateEditor/ScreenFlow';
import {
  PathHighlightInformation,
  ScreenFlowConnector,
} from 'dashboard/apps/InquiryTemplateEditor/ScreenFlow/ScreenFlowElements';
import ScreenFlowNodeWrapper from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowNodeWrapper';
import ExpandableScreenFlowNodeWrapper from 'dashboard/apps/InquiryTemplateEditor/ScreenFlowNodeWrapper/ExpandableScreenFlowNodeWrapper';
import ScreenTransitionNode from 'dashboard/apps/InquiryTemplateEditor/ScreenTransitionNode';
import { uiStepAllComponents } from 'dashboard/apps/InquiryTemplateEditor/utils/steps';
import WrappedConnectorNode from 'dashboard/apps/InquiryTemplateEditor/WrappedConnectorNode';
import { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import { ThemeAttributes } from 'dashboard/models/theme';
import { WorkflowStepType } from 'dashboard/models/workflow-version';
import { StepUi, UiStepType } from 'lib/interfaces/StepsConfig';
import { isTextComponent, isUiTransitionComponent } from 'lib/interfaces/ui-components';
import { captureMessage } from 'lib/reporting';

export const getConditionalRefId = (token: string, index: number): string => {
  return `${token}.${index}`;
};

export const getScreenRefId = (token: string, parentWorkflowNodeToken?: string): string => {
  return parentWorkflowNodeToken ? `${token}.${parentWorkflowNodeToken}` : token;
};

interface InquiryRunTreeV2Props {
  tree: CombinedRunStepForTree[];
  workflowVersionMap: WorkflowVersionMap;
  theme: ThemeAttributes;
}

/**
 * This component is responsible for rendering an Inquiry run tree
 * within the Inquiry details page to display the path that a user
 * took through an Inquiry flow created via an InquiryTemplateVersion.
 *
 * Note: This is based off of InquiryTemplateTree which renders a preview of
 * the InquiryTemplateVersion's screens and possible paths, which is used to
 * understand and configure the template.
 *
 * This tree is intended to render an identical tree given the same InquiryTemplateVersion,
 * so if a fix or change is made here, please check if InquiryTemplateTree needs
 * to be updated for parity, and vice versa
 */
const InquiryRunTree = ({ tree, workflowVersionMap, theme }: InquiryRunTreeV2Props) => {
  const { zoomableRef } = usePanZoomBoxControls();
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const { saveRef } = useInquiryTemplateTreeContent();
  const dashboardTheme = useContext(ThemeContext);
  const onRunPathColor = dashboardTheme.color.primary.brand600;
  const screenHeightWithPadding = stepContentHeight + calculateScreenFlowPadding(stepContentHeight);

  const renderCard = useCallback(
    (
      step: CombinedRunStepForTree,
      _previousStep?: CombinedRunStepForTree,
      _previousStepBranchIndex?: number,
      _nextStep?: CombinedRunStepForTree,
      parentWorkflowNodeToken?: string,
    ) => {
      const { dataMarketingId, dataName, dataTest, title } = getNodeWrapperAttributes(step);
      const screenRefId = getScreenRefId(step.token, parentWorkflowNodeToken);
      let node: ReactNode = null;
      let expandNodes: ReactElement[] = [];
      let withSticker = true;

      if (step.type === 'workflow') {
        switch (step.workflowStep.type) {
          case WorkflowStepType.Action:
          case WorkflowStepType.Placeholder:
          case WorkflowStepType.Parallel: {
            node = (
              <ActionsNode
                title={title}
                workflowVersionId={step.workflowVersionId}
                workflowStepToken={step.workflowStep.token}
                selected={step.isInRunPath}
                onActionsDoubleClick={noop}
              />
            );
            break;
          }
          case WorkflowStepType.Conditional: {
            withSticker = false;
            node = <ConditionalRootNode selected={step.isInRunPath} />;
            break;
          }
          default:
            return <></>;
        }
      } else {
        if (step.isConnector) {
          return (
            <div ref={(ref) => saveRef(ref, step, screenRefId)}>
              <WrappedConnectorNode
                connectingStep={step}
                dataName={dataName}
                dataTest={dataTest}
                dataMarketingId={dataMarketingId}
                omitFromRun={!step.isInRunPath}
                handleConnectingStepChange={noop}
                selected={step.isInRunPath}
                editable={false}
                steps={[]}
                title={title}
                theme={theme}
              />
            </div>
          );
        } else {
          switch (step.type) {
            case UiStepType.Complete: {
              node = <CompleteStepNode selected={step.isInRunPath} />;
              break;
            }
            case UiStepType.Document:
            case UiStepType.GovernmentId:
            case UiStepType.Integration:
            case UiStepType.VerifyWithPersona:
            case UiStepType.Selfie:
            case UiStepType.SelfieReflection:
            case UiStepType.Ui:
            case UiStepType.Loop: {
              node = (
                <InquiryTemplateScreenPreview
                  page={step.page}
                  step={step.stepPreview}
                  theme={theme}
                  selected={step.isInRunPath}
                />
              );
              expandNodes = (step?.combinedStepPages ?? []).map((page) => (
                <InquiryTemplateScreenPreview
                  key={page.key}
                  page={page}
                  step={step.stepPreview}
                  theme={theme}
                />
              ));
              break;
            }
          }
        }
      }

      const commonScreenWrapperProps = {
        ref: (ref: HTMLDivElement) => saveRef(ref, step, screenRefId),
        node,
        title,
        'data-name': dataName,
        'data-test': dataTest,
        'data-marketing-id': dataMarketingId,
        selected: step.isInRunPath,
        omitFromRun: !step.isInRunPath,
        editable: false,
        withSticker,
      };

      return expandNodes.length > 0 ? (
        <ExpandableScreenFlowNodeWrapper {...commonScreenWrapperProps} expandNodes={expandNodes} />
      ) : (
        <ScreenFlowNodeWrapper {...commonScreenWrapperProps} />
      );
    },
    [saveRef, theme],
  );

  const renderConnector = useCallback(
    (prevStep: CombinedRunStepForTree, nextStep: CombinedRunStepForTree | undefined) =>
      nextStep != null ? (
        <ScreenFlowConnector
          height={screenHeightWithPadding}
          color={prevStep.isInRunPath && nextStep.isInRunPath ? onRunPathColor : undefined}
        />
      ) : (
        <></>
      ),
    [onRunPathColor, screenHeightWithPadding],
  );

  const renderBranchRootConnector = useCallback(
    (rootStep: CombinedRunStepForTree, branchIndex: number, runPathIndexes?: number[]) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      const nextStep: CombinedRunStepForTree =
        rootStep.branches?.length && rootStep.branches[branchIndex]?.length
          ? rootStep.branches[branchIndex][0]
          : null;
      const isInRunPath = runPathIndexes?.includes(branchIndex);
      const uniqueId = getConditionalRefId(rootStep.token, branchIndex);
      let node: ReactNode = null;
      let title = '';
      let dataTest = '';

      if (
        rootStep.type === 'workflow' &&
        rootStep.workflowStep.type === WorkflowStepType.Conditional
      ) {
        const workflowStep = getConditionalStepForToken(
          rootStep.workflowStep.token,
          workflowVersionMap[rootStep.workflowVersionId],
        );
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        title = workflowStep.conditionals[branchIndex]?.name ?? `Route ${branchIndex + 1}`;
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        dataTest = `${workflowStep.token}__${workflowStep.type}-branch-${branchIndex}-node`;
        node = <ConditionalNode title={title} selected={isInRunPath} />;
      } else {
        dataTest = `${rootStep.token}__${rootStep.type}-branch-${branchIndex}-screen-step-node`;
        switch (rootStep.type) {
          case UiStepType.Document:
          case UiStepType.GovernmentId:
          case UiStepType.Selfie:
          case UiStepType.SelfieReflection: {
            if (branchIndex === 0) {
              if (nextStep?.type === 'workflow') {
                title = nextStep.workflowStep.name;
              } else {
                // combined steps should only ever branch because workflow_version_id and workflow_on_error_step_name are set
                captureMessage(
                  `Rendering flow tree branches from combined step: ${rootStep.name}, but nextStep is not type workflow`,
                  'warning',
                );
                return <></>;
              }
            } else {
              title = workflowErrorRouteLabel;
            }
            const description =
              title === workflowErrorRouteLabel ? workflowErrorRouteDescription : null;
            node = (
              <ScreenTransitionNode
                title={title}
                /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                description={description}
                selected={isInRunPath}
              />
            );
            break;
          }
          case UiStepType.Ui: {
            const transitionComponent = uiStepAllComponents(rootStep.stepPreview as StepUi).filter(
              isUiTransitionComponent,
            )?.[branchIndex];
            const title = isTextComponent(transitionComponent)
              ? transitionComponent.attributes.text
              : `Route ${branchIndex + 1}`;
            node = <ScreenTransitionNode title={title} selected={isInRunPath} />;
            break;
          }
          default:
            return <></>;
        }
      }

      return (
        <ConditionalWrapper>
          <ScreenFlowNodeWrapper
            ref={(ref) => saveRef(ref, rootStep, uniqueId)}
            node={node}
            title={title}
            data-name={kebabCase(title)}
            data-test={dataTest}
            data-marketing-id='ite-branch-tree-card'
            omitFromRun={!isInRunPath}
            editable={false}
          />
          <ScreenFlowConnector
            height={screenHeightWithPadding}
            color={isInRunPath && nextStep.isInRunPath ? onRunPathColor : undefined}
          />
        </ConditionalWrapper>
      );
    },
    [onRunPathColor, saveRef],
  );

  /**
   * Used to style ScreenFlowBranchConnectors when highlighting the
   * appropriate portions of the branch on the run path and omitting the rest
   */
  const pathHighlightInformationForStep = useCallback(
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    (currentStep: CombinedRunStepForTree): PathHighlightInformation => {
      if (
        Array.isArray(currentStep.branches) &&
        currentStep.isInRunPath &&
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        currentStep.chosenIndexes?.length > 0
      ) {
        const runPathIndexes = currentStep.chosenIndexes ?? [];
        const childTokens = currentStep.branches.map((branchSteps) =>
          branchSteps.length > 0 ? branchSteps[0].token : '',
        );
        const highlightPercentage =
          runPathIndexes != null && Math.max(...runPathIndexes) > 0
            ? (100 / (childTokens.length - 1)) * Math.max(...runPathIndexes)
            : undefined;

        return {
          highlightColor: onRunPathColor,
          runPathIndexes: runPathIndexes,
          highlightPercentage: highlightPercentage,
        };
      }
    },
    [onRunPathColor],
  );

  return (
    <ScreenFlow
      innerRef={zoomableRef}
      steps={tree}
      stepContentHeight={stepContentHeight}
      stepContentWidth={stepContentWidth}
      pathHighlightInformationForStep={pathHighlightInformationForStep}
      renderStep={renderCard}
      renderConnector={renderConnector}
      renderBranchRootConnector={renderBranchRootConnector}
    />
  );
};

InquiryRunTree.Navigation = InquiryRunTreeNavigation;

export default InquiryRunTree;
